import React, {useRef, useState} from "react";
import {Box, Typography, Button, Avatar, IconButton, Menu, MenuItem, ListItemIcon, ListItemText} from "@mui/material";
import {MAIN_COLOR_RED} from "../../assets/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import FolderIcon from "@mui/icons-material/Folder";
import EditIcon from "@mui/icons-material/Edit";

interface Product {
    id: number;
    title: string;
    description: string;
    discount?: string | null;
    price: number;
    quantity: number;
    image: string;
}

interface SavedShoppingList {
    id: string;
    name: string;
    items: Record<string, number>;
    createdAt: string; // ISO string
    updatedAt: string; // ISO string
}

interface ShoppingListDrawerProps {
    selectedProducts: Product[];
    totalPrice: number;
    totalDiscount: number;
    onCheckout?: () => void;
    onRemoveItem?: (id: number) => void;
    onSaveList?: () => void;
    onLoadList?: (list: SavedShoppingList) => void;
    onEditList?: (list: SavedShoppingList) => void;
    onDeleteList?: (listId: string) => void;
    savedLists?: SavedShoppingList[];
    canSave?: boolean;
}

const ShoppingListDrawer: React.FC<ShoppingListDrawerProps> = ({
                                                                   selectedProducts,
                                                                   totalPrice,
                                                                   totalDiscount,
                                                                   onCheckout,
                                                                   onRemoveItem,
                                                                   onSaveList,
                                                                   onLoadList,
                                                                   onEditList,
                                                                   onDeleteList,
                                                                   savedLists = [],
                                                                   canSave = false,
                                                               }) => {
    const [expanded, setExpanded] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

    const startY = useRef<number | null>(null);

    const handleTouchStart = (e: React.TouchEvent) => {
        startY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (startY.current === null) return;
        
        const endY = e.changedTouches[0].clientY;
        const deltaY = endY - startY.current;
        
        if (deltaY < -50) {
            setExpanded(true);
        } else if (deltaY > 50) {
            setExpanded(false);
        }
        
        startY.current = null;
    };

    const handleClick = () => {
        setExpanded(!expanded);
    };


    return (
        <Box
            sx={{
                position: "fixed",
                bottom: "70px", // Positioned above bottom navigation
                left: 0,
                right: 0,
                height: expanded ? "50vh" : "60px",
                backgroundColor: "#fff",
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                borderBottomLeftRadius: 16,
                borderBottomRightRadius: 16,
                boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
                transition: "height 0.3s",
                zIndex: 999, // Below bottom navigation (1001) to prevent pushing it
                overflow: "hidden",
                touchAction: "none",
                userSelect: "none",
                maxHeight: "calc(100vh - 140px)", // Adjusted for closer positioning
                transform: "translateZ(0)", // Force hardware acceleration
                willChange: "height", // Optimize height transitions
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onClick={handleClick}
        >
            {/* Drag Handle - Now clickable */}
            <Box
                sx={{
                    width: 40,
                    height: 4,
                    backgroundColor: "#ccc",
                    borderRadius: 2,
                    mx: "auto",
                    my: 1,
                    cursor: "pointer",
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(!expanded);
                }}
            />

            {/* Drawer Content */}
            <Box
                px={2}
                pb={expanded ? 8 : 2}
                sx={{ 
                    height: "100%", 
                    overflowY: "auto",
                    cursor: "grab",
                    "&:active": {
                        cursor: "grabbing"
                    },
                    pointerEvents: "auto"
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Shopping List</Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box
                            sx={{
                                backgroundColor: "gold",
                                px: 1,
                                py: 0.5,
                                borderRadius: "4px",
                                fontSize: "0.5rem",
                                fontWeight: "bold",
                            }}
                        >
                            Desconto / Poupança
                        </Box>
                        <Typography variant="body2">€ {totalDiscount.toFixed(2)}</Typography>
                        <Typography variant="h6">€ {totalPrice.toFixed(2)}</Typography>
                    </Box>
                </Box>

                {/* Save and Load Buttons */}
                <Box display="flex" gap={1} mb={2}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={onSaveList}
                        disabled={!canSave}
                        sx={{ 
                            borderColor: MAIN_COLOR_RED, 
                            color: MAIN_COLOR_RED,
                            flex: 1
                        }}
                    >
                        Save List
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FolderIcon />}
                        onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                        disabled={savedLists.length === 0}
                        sx={{ 
                            borderColor: MAIN_COLOR_RED, 
                            color: MAIN_COLOR_RED
                        }}
                    >
                        Load List
                    </Button>
                </Box>

                {selectedProducts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No items selected yet.
                    </Typography>
                ) : (
                    selectedProducts.map((item) => (
                        <Box
                            key={item.id}
                            display="flex"
                            gap={2}
                            mb={2}
                            alignItems="flex-start"
                            sx={{ position: "relative" }}
                        >
                            <Avatar
                                variant="square"
                                src={item.image}
                                alt={item.title}
                                sx={{ width: 48, height: 48 }}
                            />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2">{item.title}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {item.quantity} × €{item.price.toFixed(2)} = €{(item.quantity * item.price).toFixed(2)}
                                </Typography>
                                {item.discount && (
                                    <Typography variant="caption" color="error">
                                        {item.discount}
                                    </Typography>
                                )}
                            </Box>
                            {onRemoveItem && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveItem(item.id);
                                    }}
                                    sx={{
                                        color: MAIN_COLOR_RED,
                                        "&:hover": {
                                            backgroundColor: "rgba(220, 38, 38, 0.1)",
                                        },
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Box>
                    ))
                )}
            </Box>

            {/* Optional Checkout Button */}
            {expanded && onCheckout && (
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 8,
                        left: 0,
                        width: "95%",
                        px: 2,
                    }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={onCheckout}
                        disabled={selectedProducts.length === 0}
                        sx={{ backgroundColor: MAIN_COLOR_RED, borderRadius: "30px", py: 1.5 }}
                    >
                        Checkout
                    </Button>
                </Box>
            )}

            {/* Saved Lists Menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={() => setMenuAnchorEl(null)}
                PaperProps={{
                    sx: { minWidth: 250 }
                }}
            >
                {savedLists.map((list) => (
                    <MenuItem key={list.id} onClick={() => {
                        onLoadList?.(list);
                        setMenuAnchorEl(null);
                    }}>
                        <ListItemIcon>
                            <FolderIcon />
                        </ListItemIcon>
                        <ListItemText 
                            primary={list.name}
                            secondary={`${Object.keys(list.items).length} items • ${new Date(list.updatedAt).toLocaleDateString()}`}
                        />
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEditList?.(list);
                                }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteList?.(list.id);
                                }}
                                sx={{ color: 'error.main' }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default ShoppingListDrawer;
